import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const fetchTemplates = createAsyncThunk(
  'templates/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.templates.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTemplate = createAsyncThunk(
  'templates/create',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await apiService.templates.create(templateData);
      // Return the full response for access to message, but also normalize data
      return {
        data: response.data.data,     // The template object
        message: response.data.message, // Success/info message from server
        success: response.data.success
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const submitTemplateToMeta = createAsyncThunk(
  'templates/submitToMeta',
  async ({ templateId, accessToken, wabaId }, { rejectWithValue }) => {
    try {
      const response = await apiService.templates.submitToMeta(templateId, { 
        accessToken, 
        wabaId 
      });
      // Return the full response for access to message, but also normalize data
      return {
        data: response.data.data,     // The template object
        message: response.data.message,
        success: response.data.success
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'templates/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.templates.update(id, data);
      // Return the full response for access to message, but also normalize data
      return {
        data: response.data.data,     // The template object
        message: response.data.message,
        success: response.data.success
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  'templates/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.templates.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const syncTemplate = createAsyncThunk(
  'templates/sync',
  async ({ id, accessToken }, { rejectWithValue }) => {
    try {
      const response = await apiService.templates.sync(id);
      // Return the full response for access to message, but also normalize data
      return {
        data: response.data.data,     // The template object
        message: response.data.message,
        success: response.data.success
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendTestMessage = createAsyncThunk(
  'templates/sendTest',
  async ({ templateId, recipientPhone, variableValues }, { rejectWithValue }) => {
    try {
      // Call new test-send endpoint: POST /templates/:templateId/test-send
      const response = await apiService.templates.sendTest(templateId, {
        templateId,
        recipientPhone,
        variableValues
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  templates: [],
  currentTemplate: null,
  loading: false,
  error: null,
  filters: {
    category: 'all',
    status: 'all',
    search: '',
  },
};

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload;
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    updateTemplateStatus: (state, action) => {
      const { id, status } = action.payload;
      const template = state.templates.find(t => t.id === id);
      if (template) {
        template.status = status;
      }
      if (state.currentTemplate?.id === id) {
        state.currentTemplate.status = status;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch templates
      .addCase(fetchTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.data || action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create template
      .addCase(createTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.loading = false;
        // Extract template data and add to list
        const template = action.payload.data;
        if (template) {
          state.templates.unshift(template);
        }
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit to Meta
      .addCase(submitTemplateToMeta.fulfilled, (state, action) => {
        const template = state.templates.find(t => t.id === action.payload.id);
        if (template) {
          template.status = 'PENDING';
          template.submittedAt = new Date().toISOString();
        }
      })
      // Update template
      .addCase(updateTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const template = action.payload.data;
        if (template) {
          const index = state.templates.findIndex(t => t._id === template._id || t.id === template.id);
          if (index !== -1) {
            state.templates[index] = template;
          }
          if (state.currentTemplate?._id === template._id || state.currentTemplate?.id === template.id) {
            state.currentTemplate = template;
          }
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete template
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(t => t._id !== action.payload && t.id !== action.payload);
      })
      // Sync template with Meta
      .addCase(syncTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncTemplate.fulfilled, (state, action) => {
        state.loading = false;
        const template = action.payload.data;
        if (template) {
          const index = state.templates.findIndex(t => t._id === template._id || t.id === template.id);
          if (index !== -1) {
            state.templates[index] = template;
          }
          if (state.currentTemplate?._id === template._id) {
            state.currentTemplate = template;
          }
        }
      })
      .addCase(syncTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Send test message
      .addCase(sendTestMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendTestMessage.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(sendTestMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setFilters,
  setCurrentTemplate,
  clearCurrentTemplate,
  updateTemplateStatus,
  clearError,
} = templatesSlice.actions;
export default templatesSlice.reducer;

